import {cva, VariantProps} from 'class-variance-authority';
import * as React from 'react';
import {Drawer as DrawerPrimitive} from 'vaul';
import {cn} from '../lib/utils';

export type DrawerProps = React.ComponentProps<typeof DrawerPrimitive.Root>;

// vaul captures the pointer on press anywhere in the content unless handleOnly,
// which on Windows pins a drag-selection to the paragraph it started in.
function Drawer({handleOnly = true, ...props}: DrawerProps) {
  return (
    <DrawerPrimitive.Root
      data-slot="drawer"
      handleOnly={handleOnly}
      {...props}
    />
  );
}
function DrawerTrigger({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Trigger>) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}
function DrawerPortal({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Portal>) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}
function DrawerClose({
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Close>) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

type DrawerOverlayProps = React.ComponentProps<typeof DrawerPrimitive.Overlay>;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  DrawerOverlayProps
>(({className, ...props}, ref) => {
  return (
    <DrawerPrimitive.Overlay
      ref={ref}
      data-slot="drawer-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
        className,
      )}
      {...props}
    />
  );
});
DrawerOverlay.displayName = 'DrawerOverlay';

interface DrawerContentProps
  extends React.ComponentProps<typeof DrawerPrimitive.Content> {
  className?: string;
  OverlayProps?: DrawerOverlayProps;
  showHandle?: boolean;
}

const drawerVariants = cva(
  cn(
    'group/drawer-content bg-background fixed z-50 flex h-auto flex-col',
    'data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg data-[vaul-drawer-direction=top]:border-b',
    'data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-12 data-[vaul-drawer-direction=bottom]:max-h-[90vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg data-[vaul-drawer-direction=bottom]:border-t',
    'data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:border-l data-[vaul-drawer-direction=right]:rounded-lg sm:data-[vaul-drawer-direction=right]:max-w-xl',
    'data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:border-r sm:data-[vaul-drawer-direction=left]:max-w-lg',
    'focus:outline-hidden',
    // Inset from top and bottom (navbar + bottom padding)
    'data-[vaul-drawer-direction=right]:bottom-1 data-[vaul-drawer-direction=right]:top-12',
    // Inset from right (for right padding)
    'data-[vaul-drawer-direction=right]:right-1 [--initial-transform:calc(100%_+_(--spacing(2)))]',
    // Hide the weird ::after that vaul adds
    'after:hidden',
  ),
  {
    variants: {
      variant: {
        default: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function DrawerContent({
  className,
  children,
  OverlayProps,
  variant = 'default',
  showHandle = true,
  ...props
}: DrawerContentProps & VariantProps<typeof drawerVariants>) {
  return (
    <DrawerPortal>
      <DrawerOverlay
        {...OverlayProps}
        className={cn(OverlayProps?.className)}
      />
      <DrawerPrimitive.Content
        data-slot="drawer-content"
        className={cn(drawerVariants({variant}), className)}
        {...props}
      >
        {showHandle && (
          <div className="bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
        )}
        {children}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  );
}
function DrawerHeader({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-header"
      className={cn(
        'flex flex-col gap-0.5 p-4 group-data-[vaul-drawer-direction=bottom]/drawer-content:text-center group-data-[vaul-drawer-direction=top]/drawer-content:text-center md:gap-1.5 md:text-left',
        className,
      )}
      {...props}
    />
  );
}
function DrawerFooter({className, ...props}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn('mt-auto flex flex-col gap-2 p-4', className)}
      {...props}
    />
  );
}
function DrawerTitle({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Title>) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn('text-foreground font-semibold', className)}
      {...props}
    />
  );
}
function DrawerDescription({
  className,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Description>) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}
export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
